import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, Search, AlertTriangle, Clock, Ban, XCircle, CheckCircle2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CREDENTIAL_ID_RE = /^FD2G-FAC-\d{4}-\d{4}$/;

const STATUS_META = {
  "ACTIVE": {
    icon: CheckCircle2,
    label: "ACTIVE",
    heading: "This credential is active and in good standing.",
    tone: "bg-emerald-50 text-emerald-800 border-emerald-200",
    pill: "bg-emerald-600 text-white",
    bar: "bg-emerald-600",
  },
  "EXPIRING SOON": {
    icon: Clock,
    label: "EXPIRING SOON",
    heading: "This credential is active but approaches its renewal date.",
    tone: "bg-amber-50 text-amber-900 border-amber-200",
    pill: "bg-amber-500 text-white",
    bar: "bg-amber-500",
  },
  "EXPIRED": {
    icon: AlertTriangle,
    label: "EXPIRED",
    heading: "This credential has expired and is not currently active.",
    tone: "bg-slate-100 text-slate-800 border-slate-300",
    pill: "bg-slate-700 text-white",
    bar: "bg-slate-700",
  },
  "SUSPENDED": {
    icon: Ban,
    label: "SUSPENDED",
    heading: "This credential is suspended and is not currently valid.",
    tone: "bg-amber-50 text-amber-900 border-amber-300",
    pill: "bg-amber-700 text-white",
    bar: "bg-amber-700",
  },
  "REVOKED": {
    icon: XCircle,
    label: "REVOKED",
    heading: "This credential has been revoked and is no longer valid.",
    tone: "bg-rose-50 text-rose-900 border-rose-200",
    pill: "bg-rose-700 text-white",
    bar: "bg-rose-700",
  },
};

function fmtDate(iso) {
  if (!iso) return "\u2014";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch (_) {
    return iso;
  }
}

export default function AcademyFd2gFacVerify() {
  const { credential_id: paramId } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState(paramId || "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { credential, program, disclaimer }
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState(false);
  const inputRef = useRef(null);

  const normalize = (raw) => (raw || "").trim().toUpperCase();

  const runLookup = useCallback(async (rawId) => {
    const cid = normalize(rawId);
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/academy/fd2g-fac-001/verify/${encodeURIComponent(cid)}`);
      setResult(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" && detail
        ? detail
        : "Credential not found. Please verify the Credential ID and try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  // On mount: if a direct-link credential_id is present in the URL, auto-run.
  useEffect(() => {
    if (paramId) {
      setInput(paramId);
      runLookup(paramId);
    }
  }, [paramId, runLookup]);

  const onSubmit = (e) => {
    e.preventDefault();
    setTouched(true);
    const cid = normalize(input);
    if (!cid) {
      setError("Please enter a Credential ID.");
      inputRef.current?.focus();
      return;
    }
    // Push the ID into the URL for shareable direct-link behavior.
    navigate(`/academy/fd2g-fac-001/verify/${encodeURIComponent(cid)}`, { replace: false });
    runLookup(cid);
  };

  const inputValidShape = !touched || !input || CREDENTIAL_ID_RE.test(normalize(input));
  const statusKey = result?.credential?.status || null;
  const statusMeta = statusKey ? STATUS_META[statusKey] : null;

  return (
    <div className="min-h-screen bg-[#F8F6EF]" data-testid="fd2gfac-verify-page">
      {/* Header strip */}
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between gap-4">
          <a href="/academy/fd2g-fac-001" className="flex items-center gap-3 group" aria-label="FD2G-FAC-001 program home">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#4A6FA5] text-white">
              <ShieldCheck className="w-5 h-5" />
            </span>
            <span className="hidden sm:block">
              <span className="block text-[10px] uppercase tracking-[0.24em] text-[#8A7238] font-semibold">FROM DIAGNOSIS TO GOODBYE</span>
              <span className="block font-heading text-slate-900">Credential Verification</span>
            </span>
          </a>
          <a
            href="/academy/fd2g-fac-001"
            className="text-sm text-slate-600 hover:text-[#4A6FA5] font-medium"
            data-testid="fd2gfac-verify-program-link"
          >
            About the program
          </a>
        </div>
      </div>

      {/* Hero + form */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pt-10 sm:pt-14">
        <p className="text-[11px] uppercase tracking-[0.26em] text-[#4A6FA5] font-semibold">Public Credential Verification</p>
        <h1 className="mt-2 font-heading text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight tracking-tight">
          Verify a FD2G Certified Facilitator credential.
        </h1>
        <p className="mt-4 text-slate-700 text-base sm:text-lg leading-relaxed">
          Enter the Credential ID exactly as it appears (for example{" "}
          <span className="font-mono text-slate-900">FD2G-FAC-2026-0001</span>). Verification returns
          the current authoritative status of the credential and the designated facilitator.
        </p>

        <form onSubmit={onSubmit} className="mt-7" noValidate>
          <label htmlFor="fd2g-verify-input" className="block text-xs uppercase tracking-[0.18em] text-slate-600 font-semibold mb-2">
            Credential ID
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              id="fd2g-verify-input"
              ref={inputRef}
              type="text"
              inputMode="text"
              autoComplete="off"
              spellCheck={false}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="FD2G-FAC-2026-0001"
              aria-invalid={!inputValidShape}
              aria-describedby="fd2g-verify-input-help"
              className={`flex-1 px-4 py-3 rounded-lg border bg-white font-mono text-slate-900 text-base focus:outline-none focus:ring-2 focus:ring-[#4A6FA5] focus:border-[#4A6FA5] ${inputValidShape ? "border-slate-300" : "border-rose-400"}`}
              data-testid="fd2gfac-verify-input"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="fd2gfac-verify-submit-btn"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#4A6FA5] text-white font-semibold hover:bg-[#3d5d8a] disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Verifying…
                </>
              ) : (
                <>
                  <Search className="w-4 h-4" /> Verify credential
                </>
              )}
            </button>
          </div>
          <p id="fd2g-verify-input-help" className="mt-2 text-xs text-slate-500">
            Credential IDs follow the format FD2G-FAC-YYYY-NNNN. Verification requires the exact ID.
          </p>
        </form>

        {/* Non-reproducible-information guardrail */}
        <p className="mt-3 text-[11px] text-slate-400 leading-relaxed">
          For privacy, this verifier does not disclose whether any particular person is enrolled in the
          program, has attempted certification, or has ever held a credential. Only credentials with a
          verified authoritative record are shown.
        </p>
      </section>

      {/* Result / error */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-16 mt-8">
        {error && !loading && (
          <div
            role="alert"
            data-testid="fd2gfac-verify-error"
            className="rounded-xl border border-slate-200 bg-white px-5 py-6 flex items-start gap-4"
          >
            <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-slate-100 text-slate-500 shrink-0">
              <AlertTriangle className="w-5 h-5" />
            </span>
            <div>
              <p className="font-heading text-lg text-slate-900">Credential not found</p>
              <p className="mt-1 text-sm text-slate-700 leading-relaxed">{error}</p>
              <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
                If you were given a Credential ID directly by a facilitator, double-check for formatting
                such as the year and number. We do not reveal account information for IDs that do not
                match an issued credential.
              </p>
            </div>
          </div>
        )}

        {result && !loading && statusMeta && (
          <div
            data-testid="fd2gfac-verify-result"
            className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden"
          >
            <div className={`${statusMeta.bar} h-1.5 w-full`} aria-hidden="true" />
            <div className="px-5 sm:px-8 py-6 sm:py-7">
              <div className="flex items-start gap-4">
                <span className={`inline-flex items-center justify-center w-11 h-11 rounded-full ${statusMeta.pill} shrink-0`}>
                  <statusMeta.icon className="w-5 h-5" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#8A7238]">Verification record</p>
                  <p className="mt-1 font-heading text-xl sm:text-2xl text-slate-900 leading-snug">
                    {statusMeta.heading}
                  </p>
                </div>
                <span
                  data-testid="fd2gfac-verify-status-pill"
                  className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${statusMeta.pill} text-[11px] font-semibold uppercase tracking-[0.14em]`}
                >
                  {statusMeta.label}
                </span>
              </div>

              <div className="mt-6 grid gap-x-6 gap-y-4 sm:grid-cols-2">
                <Field label="Facilitator" testId="fd2gfac-verify-facilitator-name">
                  {result.credential.facilitator_name || "\u2014"}
                </Field>
                <Field label="Credential Designation" testId="fd2gfac-verify-credential-name">
                  {result.credential.credential_name}
                </Field>
                <Field label="Credential ID" testId="fd2gfac-verify-credential-id">
                  <span className="font-mono">{result.credential.credential_id}</span>
                </Field>
                <Field label="Credential Code" testId="fd2gfac-verify-credential-code">
                  {result.credential.credential_code}
                </Field>
                <Field label="Original Issue Date" testId="fd2gfac-verify-original-issue">
                  {fmtDate(result.credential.original_issue_date)}
                </Field>
                <Field label="Valid Through" testId="fd2gfac-verify-valid-through">
                  {fmtDate(result.credential.valid_through)}
                </Field>
              </div>

              <div className={`mt-6 rounded-lg border px-4 py-3 text-sm leading-relaxed ${statusMeta.tone}`}>
                {result.disclaimer || "This is the authoritative verification record for the credential shown."}
              </div>
            </div>
          </div>
        )}

        {/* Principle footer — present on both result and empty states for brand consistency. */}
        <div className="mt-10 text-center">
          <p className="text-[11px] uppercase tracking-[0.26em] text-[#8A7238] font-semibold">From Diagnosis to Goodbye</p>
          <p className="mt-2 font-heading text-slate-900 italic">
            See the person. Hear the voice. Honor the journey.
          </p>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500">{label}</p>
      <p className="mt-1 text-slate-900" data-testid={testId}>{children}</p>
    </div>
  );
}
